import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2, Zap, RefreshCcw, DollarSign, Crown, Users, Ticket, TrendingUp, Gift, Undo2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (c) => `$${((c || 0) / 100).toFixed(2)}`;

const MODELS = [
  { id: 'milestone_refund', icon: RefreshCcw, title: 'Bring Friends, Go Free', desc: 'Refer N buyers → automatic 100% refund of their base ticket via Stripe.' },
  { id: 'cash_kickback', icon: DollarSign, title: 'Instant Cash Kickback', desc: '$X auto-refunded per referred ticket, up to their full ticket price.' },
  { id: 'perk', icon: Crown, title: 'On-Site Perk', desc: 'Refer N buyers → unlock a perk shown to door staff at QR scan. Zero cash outlay.' },
];

export const KickbackConfigTab = ({ eventId, token }) => {
  const [cfg, setCfg] = useState(null);
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([
        axios.get(`${API}/events/${eventId}/kickback-config`, { headers }),
        axios.get(`${API}/events/${eventId}/kickback-stats`, { headers }),
      ]);
      setCfg(c.data); setStats(s.data);
    } catch { toast.error('Could not load kickback settings'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, token]);

  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/events/${eventId}/kickback-config`, { ...cfg, ...patch }, { headers });
      setCfg(r.data);
      toast.success('Kickback settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (!cfg) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>;

  return (
    <div className="space-y-5" data-testid="kickback-config-tab">
      {/* Enable */}
      <Card className="bg-card border-border">
        <CardContent className="p-5 flex items-start justify-between gap-4">
          <div>
            <h3 className="font-semibold flex items-center gap-2"><Zap className="h-4 w-4 text-amber-400" /> Kickbacks — turn every buyer into a promoter</h3>
            <p className="text-xs text-muted-foreground mt-1 max-w-xl">
              After checkout, buyers see a personal share link. When friends buy through it, the reward settles
              automatically — no spreadsheets, no manual payouts. You only pay after the sale happens.
            </p>
          </div>
          <Switch checked={cfg.enabled} onCheckedChange={(v) => save({ enabled: v })} data-testid="kickback-enable-toggle" />
        </CardContent>
      </Card>

      {/* Reward model */}
      <div className="grid md:grid-cols-3 gap-3">
        {MODELS.map(m => {
          const Icon = m.icon;
          const active = cfg.reward_model === m.id;
          return (
            <button key={m.id} onClick={() => save({ reward_model: m.id })} disabled={saving}
              className={`text-left rounded-xl border p-4 transition ${active ? 'border-amber-500/60 bg-amber-500/5' : 'border-border bg-card hover:border-border/80'}`}
              data-testid={`kickback-model-${m.id}`}>
              <Icon className={`h-5 w-5 mb-2 ${active ? 'text-amber-400' : 'text-muted-foreground'}`} />
              <p className="font-semibold text-sm">{m.title}</p>
              <p className="text-[11px] text-muted-foreground mt-1">{m.desc}</p>
              {active && <Badge className="mt-2 bg-amber-500/15 text-amber-300 border-amber-500/30 text-[10px]">Selected</Badge>}
            </button>
          );
        })}
      </div>

      {/* Model settings */}
      <Card className="bg-card border-border">
        <CardContent className="p-5 grid sm:grid-cols-3 gap-4">
          {cfg.reward_model !== 'cash_kickback' && (
            <div>
              <label className="text-xs text-muted-foreground">Friends needed (threshold)</label>
              <Input type="number" min={1} max={50} value={cfg.threshold}
                onChange={(e) => setCfg({ ...cfg, threshold: parseInt(e.target.value || '0', 10) })}
                className="mt-1" data-testid="kickback-threshold-input" />
            </div>
          )}
          {cfg.reward_model !== 'perk' && (
            <div>
              <label className="text-xs text-muted-foreground">Tickets covered per buyer</label>
              <Input type="number" min={1} max={20} value={cfg.max_tickets_rewarded || 1}
                onChange={(e) => setCfg({ ...cfg, max_tickets_rewarded: parseInt(e.target.value || '1', 10) })}
                className="mt-1" data-testid="kickback-max-tickets-input" />
              <p className="text-[10px] text-muted-foreground mt-1">Refund covers this many of the buyer's tickets — not their whole order. Default: 1 (their own ticket)</p>
            </div>
          )}
          {cfg.reward_model === 'cash_kickback' && (
            <div>
              <label className="text-xs text-muted-foreground">Kickback per referred ticket ($)</label>
              <Input type="number" min={0.5} max={100} step={0.5} defaultValue={(cfg.kickback_cents / 100).toFixed(2)}
                onChange={(e) => setCfg({ ...cfg, kickback_cents: Math.round(parseFloat(e.target.value || '0') * 100) })}
                className="mt-1" data-testid="kickback-amount-input" />
            </div>
          )}
          {cfg.reward_model === 'perk' && (
            <div className="sm:col-span-2">
              <label className="text-xs text-muted-foreground">Perk label (shown at the scanner)</label>
              <Input value={cfg.perk_label} maxLength={80}
                onChange={(e) => setCfg({ ...cfg, perk_label: e.target.value })}
                className="mt-1" placeholder="Skip-the-Line VIP Upgrade" data-testid="kickback-perk-input" />
            </div>
          )}
          <div className="flex items-end">
            <Button size="sm" onClick={() => save({})} disabled={saving} data-testid="kickback-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save settings'}
            </Button>
          </div>
          <div className="sm:col-span-3 flex items-center justify-between rounded-lg border border-border/60 px-3 py-2.5">
            <div>
              <p className="text-xs font-medium">Include add-ons in reward value</p>
              <p className="text-[11px] text-muted-foreground">Off by default — refunds and caps count admission tickets only, never add-ons like bar wristbands</p>
            </div>
            <Switch checked={!!cfg.include_addons} onCheckedChange={(v) => save({ include_addons: v })} data-testid="kickback-include-addons-toggle" />
          </div>
        </CardContent>
      </Card>

      {/* Buyer preview */}
      <Card className="bg-card border-border">
        <CardContent className="p-5">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">What buyers see after checkout</p>
          <div className="rounded-xl p-4 text-sm font-semibold text-white" style={{ background: 'linear-gradient(135deg, #78350f, #831843, #4c1d95)' }} data-testid="kickback-preview">
            {cfg.reward_model === 'milestone_refund' && <>🎉 Going with friends? If {cfg.threshold} friends buy tickets, your ticket is 100% FREE (automatic refund to your card).</>}
            {cfg.reward_model === 'cash_kickback' && <>💸 Earn {fmt(cfg.kickback_cents)} back for every ticket a friend buys with your link — automatic refunds.</>}
            {cfg.reward_model === 'perk' && <>👑 Refer {cfg.threshold} friends and unlock: {cfg.perk_label} at the door.</>}
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      {stats && (
        <div>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-4">
            {[
              { icon: Users, label: 'Referrers', value: stats.referrers },
              { icon: Ticket, label: 'Referred orders', value: stats.referred_orders },
              { icon: Ticket, label: 'Referred tickets', value: stats.referred_tickets },
              { icon: TrendingUp, label: 'Referred revenue', value: fmt(stats.referred_revenue_cents) },
              { icon: Gift, label: 'Rewards paid', value: cfg.reward_model === 'perk' ? `${stats.perks_unlocked} perks` : fmt(stats.rewards_paid_cents) },
              { icon: Undo2, label: 'Clawbacks', value: stats.reversed_referrals > 0 ? `${stats.reversed_referrals}${stats.flagged_rewards > 0 || stats.clawed_cents > 0 ? ` · ${fmt(stats.clawed_cents)} flagged` : ''}` : '0' },
            ].map((k, i) => (
              <Card key={i} className="bg-card border-border"><CardContent className="p-3">
                <k.icon className="h-4 w-4 text-indigo-400 mb-1" />
                <p className="text-lg font-bold" data-testid={`kickback-stat-${i}`}>{k.value}</p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{k.label}</p>
              </CardContent></Card>
            ))}
          </div>
          {stats.top_referrers.length > 0 && (
            <Card className="bg-card border-border"><CardContent className="p-4">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Top referrers</p>
              <div className="space-y-1.5">
                {stats.top_referrers.map((r, i) => (
                  <div key={i} className="flex items-center justify-between text-sm border-b border-border/40 pb-1.5" data-testid={`kickback-referrer-${i}`}>
                    <div>
                      <span className="font-medium">{r.name}</span>
                      <span className="text-xs text-muted-foreground ml-2">{r.email}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span>{r.referrals} referral{r.referrals !== 1 ? 's' : ''}</span>
                      {r.flagged && <Badge className="bg-red-500/15 text-red-300 border-red-500/30 text-[10px]">Clawback flagged</Badge>}
                      {r.perk ? <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 text-[10px]">{r.perk}</Badge>
                        : <span className="font-mono text-emerald-400">{fmt(r.reward_cents)}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent></Card>
          )}
        </div>
      )}
    </div>
  );
};

export default KickbackConfigTab;
